import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Visibility,
  AttachMoney,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import budgetsService, { Budget } from '../services/budgetsService';



const Budgets: React.FC = () => {
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  // Load budgets from API
  useEffect(() => {
    const loadBudgets = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await budgetsService.getBudgets();
        // Ensure data is an array
        setBudgets(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading budgets:', err);
        setError('Failed to load budgets. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadBudgets();
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (!searchTerm.trim()) return;

    const timeoutId = setTimeout(async () => {
      try {
        setLoading(true);
        const data = await budgetsService.searchBudgets(searchTerm);
        setBudgets(data);
      } catch (err) {
        console.error('Error searching budgets:', err);
        setError('Failed to search budgets. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Reset search
  useEffect(() => {
    if (!searchTerm.trim()) {
      const loadBudgets = async () => {
        try {
          setLoading(true);
          const data = await budgetsService.getBudgets();
          setBudgets(data);
        } catch (err) {
          console.error('Error loading budgets:', err);
          setError('Failed to load budgets. Please try again.');
        } finally {
          setLoading(false);
        }
      };
      loadBudgets();
    }
  }, [searchTerm]);

  // Handle delete budget
  const handleDelete = async () => {
    if (!selectedBudget) return;

    try {
      await budgetsService.deleteBudget(selectedBudget.id);
      setBudgets(budgets.filter(budget => budget.id !== selectedBudget.id));
      setSnackbar({
        open: true,
        message: 'Budget deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting budget:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete budget. Please try again.',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedBudget(null);
    }
  };

  // Handle status change
  const handleStatusChange = async (budgetId: string, newStatus: Budget['status']) => {
    try {
      const updatedBudget = await budgetsService.updateBudgetStatus(budgetId, newStatus);
      setBudgets(budgets.map(budget => 
        budget.id === budgetId ? updatedBudget : budget
      ));
      setSnackbar({
        open: true,
        message: 'Budget status updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating budget status:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update budget status. Please try again.',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status: Budget['status']) => {
    switch (status) {
      case 'on-track':
        return 'success';
      case 'under-budget':
        return 'info';
      case 'at-risk':
        return 'warning';
      case 'over-budget':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: Budget['category']) => {
    switch (category) {
      case 'labor':
        return 'primary';
      case 'materials':
        return 'secondary';
      case 'equipment':
        return 'info';
      case 'overhead':
        return 'warning';
      case 'contingency':
        return 'success';
      default:
        return 'default';
    }
  };

  const getVarianceIcon = (variance: number) => {
    if (variance > 0) return <TrendingUp color="error" />;
    if (variance < 0) return <TrendingDown color="success" />;
    return <CheckCircle color="success" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Ensure budgets is an array before using array methods
  const budgetsArray = Array.isArray(budgets) ? budgets : [];
  
  const filteredBudgets = budgetsArray.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalBudgeted = budgetsArray.reduce((sum, item) => sum + (item.budgetedAmount || 0), 0);
  const totalActual = budgetsArray.reduce((sum, item) => sum + (item.actualAmount || 0), 0);
  const totalVariance = budgetsArray.reduce((sum, item) => sum + (item.variance || 0), 0);
  const overBudgetCount = budgetsArray.filter(item => item.status === 'over-budget').length;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading budgets...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Budget Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/budgets/create')}
        >
          Create Budget
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Budgeted
            </Typography>
            <Typography variant="h5" component="div">
              {formatCurrency(totalBudgeted)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Actual
            </Typography>
            <Typography variant="h5" component="div">
              {formatCurrency(totalActual)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Variance
            </Typography>
            <Typography 
              variant="h5" 
              component="div"
              color={totalVariance > 0 ? 'error.main' : 'success.main'}
            >
              {formatCurrency(totalVariance)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 200, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Over Budget
            </Typography>
            <Typography variant="h5" component="div" color="error.main">
              {overBudgetCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search budgets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Budgets Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Budget Item</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Budgeted</TableCell>
              <TableCell>Actual</TableCell>
              <TableCell>Committed</TableCell>
              <TableCell>Remaining</TableCell>
              <TableCell>Variance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBudgets.map((item) => {
              const progressPercentage = (item.actualAmount / item.budgetedAmount) * 100;
              return (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{item.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Updated: {new Date(item.lastUpdated).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.project}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.category.toUpperCase()}
                      color={getCategoryColor(item.category)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(item.budgetedAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(item.actualAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="warning.main">
                      {formatCurrency(item.committedAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2"
                      color={item.remainingAmount < 0 ? 'error.main' : 'textPrimary'}
                    >
                      {formatCurrency(item.remainingAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getVarianceIcon(item.variance)}
                      <Box>
                        <Typography 
                          variant="body2"
                          color={item.variance > 0 ? 'error.main' : 'success.main'}
                        >
                          {formatCurrency(Math.abs(item.variance))}
                        </Typography>
                        <Typography 
                          variant="caption"
                          color={item.variance > 0 ? 'error.main' : 'success.main'}
                        >
                          ({Math.abs(item.variancePercentage).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status.replace('-', ' ').toUpperCase()}
                      color={getStatusColor(item.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: 100 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(progressPercentage, 100)}
                        color={progressPercentage > 100 ? 'error' : 'primary'}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {progressPercentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/budgets/${item.id}`)}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Budget">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/budgets/${item.id}/edit`)}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Budget">
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => {
                          setSelectedBudget(item);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredBudgets.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="textSecondary">
            No budgets found
          </Typography>
          <Typography color="textSecondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Start by creating your first budget'}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedBudget(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the budget "{selectedBudget?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedBudget(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Error Display */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Budgets;